import { Box, Flex, Heading2, Heading4, Text, Tooltip } from "@nulogy/components";
import WorkBlockComponent from "components/common/Scheduler/SchedulerRenderers/components/blocks/WorkBlock";
import { sampleBlock, sampleWorkOrder } from "components/common/samplePreviewData";
import WorkBlockPreviewBox from "components/common/WorkBlockPreviewBox";
import Checkbox from "components/controls/Checkbox";
import Radio from "components/controls/Radio";
import WorkOrderCard from "components/pages/schedule/workOrderCards/WorkOrderCard";
import WorkBlockObject from "domain/WorkBlock";
import { useField } from "formik";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import Codenames from "policies/Codenames";
import React, { useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";

const SectionHeader = styled(Heading2)(({ theme }) => ({
  fontWeight: theme.fontWeights.normal,
}));

const SubHeader = styled(Text)(({ theme }) => ({
  marginBottom: theme.space.half,
  marginTop: theme.space.x3,
}));

const Columns = styled(Box)(({ theme }) => ({
  label: {
    padding: `${theme.space.half} 0`,
  },
}));

const CustomRadio = styled(Radio)(({ theme }) => ({
  label: {
    padding: `${theme.space.half} 0`,
  },
}));

const LayoutHeading = styled(Heading4)(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
  marginBottom: theme.space.half,
}));

function KeepTooltip() {
  const theme = useTheme();

  return (
    <Tooltip
      maxWidth="220px"
      placement="right"
      tooltip="If selected, this field will appear on the work order card when expanded or collapsed."
    >
      <img
        alt="If selected, this field will appear on the work order card when expanded or collapsed."
        height={theme.sizes.x2}
        src="./svg/components/pages/settings/keep.svg"
        width={theme.sizes.x2}
      />
    </Tooltip>
  );
}

const WorkBlockLayout = () =>
  WorkBlockObject.LAYOUTS.map((layout) => {
    const labelComponent = <Text>{layout.label}</Text>;

    return (
      <CustomRadio
        className={`spec-work-block-layout-${layout.value}`}
        field="workBlockLayout"
        key={`work-block-layout-${layout.value}`}
        labelText={labelComponent}
        value={layout.value}
      />
    );
  });

export default function LayoutSetting({ packmanagerCustomWorkOrderFields = [] }) {
  const [workOrderCardExpanded, setWorkOrderCardExpanded] = useState(true);

  const [workOrderCardLayoutField] = useField("workOrderCardLayout");
  const [customWorkOrderFieldsField, , customWorkOrderFieldsHelpers] = useField(
    "workOrderCardLayout.customWorkOrderFields",
  );
  const [workBlockLayoutField] = useField("workBlockLayout");

  const customWorkOrderFields = customWorkOrderFieldsField.value || [];

  // Ensure all custom work order fields have an entry in the form array
  useEffect(() => {
    const missingFields = packmanagerCustomWorkOrderFields.filter(
      (pmField) => !customWorkOrderFields.some((f) => f.fieldId === pmField.id),
    );

    if (missingFields.length > 0) {
      const newEntries = missingFields.map((pmField) => ({ enabled: false, fieldId: pmField.id }));
      customWorkOrderFieldsHelpers.setValue([...customWorkOrderFields, ...newEntries]);
    }
  }, [packmanagerCustomWorkOrderFields, customWorkOrderFields, customWorkOrderFieldsHelpers]);

  const applicationContext = useApplicationContext();
  const workBlockComponentApplicationContext = {
    ...applicationContext,
    customerSettings: { workBlockLayout: workBlockLayoutField.value },
  };

  const previewWorkOrder = useMemo(
    () => buildPreviewWorkOrder(packmanagerCustomWorkOrderFields),
    [packmanagerCustomWorkOrderFields],
  );

  return (
    <Flex flexDirection="column" gap="x4" mb="x2">
      <Box>Customize the layout of work blocks and work order cards on your production schedule.</Box>
      <Box data-testid="work-block-layout-settings">
        <Flex data-testid="work-block-layout-setting" gap="x8">
          <Box>
            <SectionHeader>Work blocks</SectionHeader>
            <SubHeader>Select what should be displayed on the first line of a work block.</SubHeader>
            <WorkBlockLayout />
          </Box>
          <Box minWidth="70px" />
          <Box mt="100px" width="350px">
            <WorkBlockPreviewBox>
              <WorkBlockComponent
                applicationContext={workBlockComponentApplicationContext}
                blockData={{
                  block: sampleBlock,
                }}
                className="underscheduled"
                showOptions={false}
              />
            </WorkBlockPreviewBox>
          </Box>
        </Flex>
      </Box>
      <Box>
        <SectionHeader>Work order cards</SectionHeader>
        <SubHeader>Select the fields that will appear on a work order card.</SubHeader>
        <Flex data-testid="work-order-card-layout-setting" gap="x8" mt="x2">
          <Box>
            <Columns>
              <LayoutHeading>Work order fields</LayoutHeading>
              <Checkbox
                className="spec-work-order-layout-field-work-order-id"
                field="workOrderCardLayout[workOrderId]"
                labelText="Work order ID"
              />
              <Checkbox
                className="spec-work-order-layout-field-work-order-description"
                field="workOrderCardLayout[workOrderDescription]"
                labelText="Work order description"
              />
              <Checkbox
                className="spec-work-order-layout-field-priority"
                field="workOrderCardLayout[workOrderPriority]"
                labelText="Work order priority"
              />
              <Checkbox
                className="spec-work-order-layout-field-material-availability-date"
                field="workOrderCardLayout[materialAvailabilityDate]"
                labelText="Material availability date"
              />
              <Checkbox
                className="spec-work-order-layout-field-planned-start-date"
                field="workOrderCardLayout[plannedStartDate]"
                labelText={`Planned start (from ${Codenames.PackmanagerShort})`}
              />
              <Checkbox
                className="spec-work-order-layout-field-planned-end-date"
                field="workOrderCardLayout[plannedEndDate]"
                labelText={`Planned end (from ${Codenames.PackmanagerShort})`}
              />
              {packmanagerCustomWorkOrderFields.map((customField) => {
                const index = customWorkOrderFields.findIndex((f) => f.fieldId === customField.id);
                if (index === -1) return null;

                return (
                  <Checkbox
                    className={`spec-work-order-layout-field-custom-work-order-field-${customField.id}`}
                    field={`workOrderCardLayout.customWorkOrderFields[${index}].enabled`}
                    key={customField.id}
                    labelText={customField.label}
                  />
                );
              })}
              <Checkbox
                className="spec-work-order-layout-field-reference-1"
                field="workOrderCardLayout[reference1]"
                labelText="Reference 1"
              />
              <Checkbox
                className="spec-work-order-layout-field-reference-2"
                field="workOrderCardLayout[reference2]"
                labelText="Reference 2"
              />
              <Checkbox
                className="spec-work-order-layout-field-reference-3"
                field="workOrderCardLayout[reference3]"
                labelText="Reference 3"
              />
              <Checkbox
                className="spec-work-order-layout-field-reference-4"
                field="workOrderCardLayout[reference4]"
                labelText="Reference 4"
              />
              <Checkbox
                className="spec-work-order-layout-field-reference-5"
                field="workOrderCardLayout[reference5]"
                labelText="Reference 5"
              />
            </Columns>
          </Box>
          <Box>
            <Columns>
              <LayoutHeading>Item fields</LayoutHeading>
              <Flex gap="half">
                <Checkbox
                  className="spec-work-order-layout-field-item-code"
                  field="workOrderCardLayout[itemCode]"
                  labelText="Item code"
                />
                <KeepTooltip />
              </Flex>
              <Flex gap="half">
                <Checkbox
                  className="spec-work-order-layout-field-item-description"
                  field="workOrderCardLayout[itemDescription]"
                  labelText="Item description"
                />
                <KeepTooltip />
              </Flex>
              <Checkbox
                className="spec-work-order-layout-field-item-class"
                field="workOrderCardLayout[itemClass]"
                labelText="Item class"
              />
              <Checkbox
                className="spec-work-order-layout-field-item-family"
                field="workOrderCardLayout[itemFamily]"
                labelText="Item family"
              />
              <Checkbox
                className="spec-work-order-layout-field-item-type"
                field="workOrderCardLayout[itemType]"
                labelText="Item type"
              />
              <Checkbox
                className="spec-work-order-layout-field-item-category"
                field="workOrderCardLayout[itemCategory]"
                labelText="Item category"
              />
              <Checkbox
                className="spec-work-order-layout-field-service-category"
                field="workOrderCardLayout[serviceCategory]"
                labelText="Service category"
              />
              <Checkbox
                className="spec-work-order-layout-field-standard-production-rate"
                field="workOrderCardLayout[standardProductionRate]"
                labelText="Standard production rate"
              />
              <Checkbox
                className="spec-work-order-layout-field-recommended-production-rate"
                field="workOrderCardLayout[recommendedProductionRate]"
                labelText="Recommended production rate"
              />
              <Checkbox
                className="spec-work-order-layout-field-standard-people"
                field="workOrderCardLayout[standardPeople]"
                labelText="Standard people"
              />
              <Checkbox
                className="spec-work-order-layout-field-recommended-people"
                field="workOrderCardLayout[recommendedPeople]"
                labelText="Recommended people"
              />
            </Columns>
          </Box>
          <Box ml="x4" width="350px">
            <WorkOrderCard
              expanded={workOrderCardExpanded}
              layoutSettingData={{ workOrderLayoutSetting: workOrderCardLayoutField.value }}
              onToggleExpandWorkOrder={() => {
                setWorkOrderCardExpanded(!workOrderCardExpanded);
              }}
              showOptions={false}
              workOrder={previewWorkOrder}
            />
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

// Generate a preview work order with custom field selections based on packmanager fields
function buildPreviewWorkOrder(packmanagerCustomWorkOrderFields) {
  const customWorkOrderFieldSelections = packmanagerCustomWorkOrderFields.map((field, index) => ({
    customWorkOrderField: { id: field.id, label: field.label },
    customWorkOrderFieldValue: { description: `Sample value ${index + 1}`, id: `value-${field.id}` },
    id: `selection-${field.id}`,
  }));

  return {
    ...sampleWorkOrder,
    customWorkOrderFieldSelections,
  };
}
